<template>
  <div>
    <div>
      <!-- page meta -->
      <Head>
        <title>Automated Coaching Quizzes in Platforma</title>
        <link
          rel="icon"
          :href="$filters.public_path('favicon.png')"
          type="favicon"
        />
        <meta
          name="description"
          content="Develop advanced AI quizzes that will automate yur coaching."
        />
      </Head>
    </div>
    <!-- content start -->

    <div
      class="min-[1140px]:max-w-[1140px] container relative mx-auto bg-transparent"
    >
      <!-- ===================== HERO SECTION ===================== -->

      <section
        id="section1"
        class="hero-section relative mx-auto mt-[150px] justify-center gap-1 bg-transparent lg:flex lg:py-0"
        :style="backgroundImageStyleMain"
      >
        <div class="mt-0 bg-transparent px-[25px] xl:px-0">
          <h1
            class="top-full mt-0 px-0 text-[35px] font-extrabold tracking-normal text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
          >
            Scale faster with on-demand coaching quizzes.
          </h1>

          <p
            class="mt-[5px] mb-[25px] text-[15px] font-medium text-[#7c828d] lg:text-lg"
          >
            Quickly create interactive quizzes with answers to common questions
            that new members benefit from.
          </p>

          <!-- captcher email widget -->
          <EmailCaptureHeaderWidget />

          <img :src="$filters.media_path('/images/stars.svg')" alt="" />
        </div>

        <div class="mb-[30px] mt-[30px] lg:mt-0 z-10 px-[15px]">
          <video
            class="lg:max-w-[618px] rounded-[10px] lg:rounded-[20px] shadow-2xl"
            id="home_video"
            loop
            muted
            autoplay
            playsinline
          >
            <source
              :src="
                $filters.media_path(
                  '/videos/quiz-builder-for-courses-and-members-com.mp4'
                )
              "
              type="video/mp4"
            />
          </video>
        </div>
      </section>

      <div
        class="join-mobile mx-auto mt-[30px] h-[150px] max-w-6xl gap-2 bg-[#f5f8fa] py-[65px] md:mt-[150px] md:flex md:h-auto md:bg-[#fafbfc] md:bg-blend-overlay"
      >
        <div class="m-auto hidden h-[1px] w-[400px] bg-[#e9ebf0] md:flex"></div>
        <h2
          class="mx-auto px-0 text-center text-[12px] font-[500] text-[#7c828d]"
        >
          JOIN 10,000+ HIGH EARNING BUSINESSES & CREATORS
        </h2>
        <div class="m-auto hidden h-[1px] w-[400px] bg-[#e9ebf0] md:flex"></div>
      </div>

      <!-- ===================== CUSTOMIZE SECTION===================== -->
      <section
        class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[50px] lg:flex lg:h-[500px] xl:px-0"
      >
        <img
          class="absolute top-10 left-[600px] z-0 hidden h-[600px] w-[810px] origin-top-right lg:block"
          :src="$filters.media_path('/images/blue_gradient.svg')"
          alt=""
        />

        <div class="z-20 mx-auto mt-12 justify-center gap-1 py-0 lg:flex">
          <div class="mt-0 text-center lg:max-w-[500px] lg:text-left">
            <div
              id="text_customize"
              class="text-xs font-extrabold text-[#2D8CFF]"
            >
              CUSTOMIZE
            </div>

            <h2
              class="top-full mt-[20px] px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
            >
              Custom training with unlimited questions & answers.
            </h2>

            <hr
              class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#2D8CFF] lg:ml-0"
            />

            <p
              class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
            >
              Streamline student results with unlimited size quizzes. They can
              be as elaborate or as simple as you want. Although the results are
              predetermined, users will feel as though the training is custom to
              their circumstance.
            </p>
          </div>

          <div class="z-30 mt-[25px] xl:mt-0">
            <img
              loading="lazy"
              class="lg:w-[650px]"
              :src="
                $filters.media_path(
                  '/images/creator-ui-of-quiz-editor-for-customers.png'
                )
              "
              alt="Setup quizzes in advance for your members to coach them without 1-on-1 work."
            />
          </div>
        </div>
      </section>

      <!-- ===================== RESULTS SECTION ===================== -->
      <section
        class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[200px] lg:flex lg:h-[500px] lg:flex-row-reverse xl:px-0"
      >
        <img
          class="absolute top-0 right-[600px] z-0 h-[600px] w-[810px] origin-top-left overflow-hidden"
          :src="$filters.media_path('/images/green-diamond-dots-bg.svg')"
          alt=""
        />

        <div
          class="mt-12 text-center lg:mx-0 lg:mt-0 lg:max-w-[500px] lg:px-[30px] lg:text-left"
        >
          <div
            id="text_results"
            class="text-xs font-extrabold text-[#27AE60] lg:left-0"
          >
            RESULTS
          </div>

          <h2
            class="top-full px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] md:mt-[20px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
          >
            Downloadable video answer after completion.
          </h2>

          <div class="">
            <hr
              class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#27AE60] lg:ml-0"
            />
          </div>

          <p
            class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
          >
            Pre-record a video specific for each unique quiz result and avoid
            answering questions live over and over again.
          </p>
        </div>

        <div
          class="mx-auto mt-12 justify-center gap-1 py-0 md:z-10 lg:mt-0 lg:px-0"
        >
          <img
            loading="lazy"
            class="lg:w-[600px]"
            :src="
              $filters.media_path(
                '/images/end-result-of-a-quiz-taken-by-a-onlice-course-buyer.png'
              )
            "
            alt="Video results members can keep."
          />
        </div>
      </section>

      <!-- ===================== MONETIZATION SECTION ===================== -->
      <section
        class="relative mt-[70px] w-full justify-center gap-2 px-[25px] lg:mx-auto lg:mt-[300px] lg:flex lg:h-[550px] xl:px-0"
      >
        <img
          class="absolute top-10 left-[600px] z-0 hidden h-[600px] w-[810px] origin-top-right lg:block"
          :src="
            $filters.media_path('/images/platforma_turqoise_diamond_bg.svg')
          "
          alt=""
        />

        <div class="z-20 mx-auto mt-12 justify-center gap-1 py-0 lg:flex">
          <div class="mt-0 text-center lg:max-w-[500px] lg:text-left">
            <div
              id="text_monetization"
              class="text-xs font-extrabold text-[#49CCF9]"
            >
              MONETIZATION
            </div>

            <h2
              class="top-full mt-[20px] px-0 text-[35px] font-bold text-[#292d34] sm:text-[40px] lg:text-[45px] lg:leading-[69px] xl:text-[50px]"
            >
              Paywall or hide multiple quizzes in your app.
            </h2>

            <hr
              class="m-auto mt-[30px] h-[5px] w-[100px] rounded-full bg-[#49CCF9] lg:ml-0"
            />

            <p
              class="mt-[50px] text-sm font-medium leading-[25px] text-[#7c828d] xl:max-w-[440px]"
            >
              Just another way Platforma keeps members engaged and continuing to
              pay for your subscription. Quizzes can be added to the same
              members area where your courses or other features reside.<br />
              Any quiz can be set for unlocking only after a member pays, OR can
              be hidden for some members and displayed to others.
            </p>
          </div>

          <div class="z-30 mt-[25px] xl:mt-0">
            <img
              loading="lazy"
              class="shadow-2xl lg:w-[625px]"
              :src="
                $filters.media_path(
                  '/images/quiz-as-an-additonal-digital-product-to-add-value-to-your-members-area.png'
                )
              "
              alt="Upsell more digital products in your members area."
            />
          </div>
        </div>
      </section>
    </div>
    <!-- ===================== CTA BLOCK ===================== -->
    <CtaBlock
      cta-headline="Start using Platforma Quizzes
to scale your coaching."
    />
  </div>
</template>

<!-- =======CONTENT END======== -->

<script>
import DefaultLayout from '../Layouts/DefaultLayout.vue';
import ButtonComponent from '@shared/Components/ButtonComponent.vue';
import Axios from 'axios';
import EmailCaptureHeaderWidget from '../Components/EmailCaptureHeader.vue';
import CtaBlock from '../Components/CtaBlockFooterComponent.vue';

export default {
  layout: DefaultLayout,
  components: {
    // ButtonComponent,
    EmailCaptureHeaderWidget,
    CtaBlock
  },

  computed: {
    backgroundImageStyleMain() {
      const imagePath = this.$filters.media_path(
        'images/platforma-header-background-eclipse.svg'
      );
      return {
        '--before-background-image': `url(${imagePath})`
      };
    }
  }
};
</script>

<style scoped>
.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  height: 1000px;
  width: 1700px;
  display: block;
  z-index: 0;
  transform: translateY(-42%) translateX(58%);
  background-repeat: no-repeat;
  background-image: var(--before-background-image);
}

.join-mobile {
  background-image: url(../media/images/two-dots.svg);
  background-size: contain;
}
</style>
